/** RFC 4180: quote any cell containing a comma, quote or newline, and double inner quotes. */
function cell(v: string | number | null): string {
  const s = String(v ?? '');
  return /["\r\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Writes a CSV file client-side. The leading BOM makes Excel read UTF-8
 * correctly instead of mangling ₹ and other non-ASCII values.
 */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null)[][]) {
  const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export interface ExportPage<T> {
  items: T[];
  total: number;
  totalPages: number;
}

/** Ceiling on a paginated export; past this the caller is told the file was cut short. */
export const MAX_EXPORT_ROWS = 5000;

/**
 * Exports every row matching the current filters, not just the visible page —
 * a finance CSV that silently stops at 20 rows is worse than no CSV at all.
 * Returns what was written; `truncated` means the ceiling was hit and the
 * caller must say so rather than imply the file is complete.
 */
export async function downloadAllCsv<T>(opts: {
  filename: string;
  header: string[];
  fetchPage: (page: number) => Promise<ExportPage<T>>;
  toRow: (item: T) => (string | number | null)[];
}): Promise<{ written: number; truncated: boolean }> {
  const rows: (string | number | null)[][] = [];
  let page = 1;
  let truncated = false;
  for (;;) {
    const res = await opts.fetchPage(page);
    rows.push(...res.items.map(opts.toRow));
    if (page >= res.totalPages) break;
    if (rows.length >= MAX_EXPORT_ROWS) {
      truncated = res.total > rows.length;
      break;
    }
    page += 1;
  }
  downloadCsv(opts.filename, opts.header, rows);
  return { written: rows.length, truncated };
}